import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { FORM_OPTIONS, TONE_OPTIONS } from '@hypo/shared'

// --- Styles ---

const presetCard = css`
  ${card};
  margin-bottom: ${space.sm};
  display: flex;
  align-items: flex-start;
  gap: ${space.md};
`

const presetInfo = css`
  flex: 1;
  min-width: 0;
`

const presetLabel = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.xs};
`

const badgeStyle = css`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  background: ${colors.bgSidebar};
  border: 1px solid ${colors.border};
  margin-right: ${space.xs};
`

const chipWrap = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: ${space.xs};
`

const chip = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  cursor: pointer;
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  user-select: none;
  transition: background 0.1s, border-color 0.1s;
  &:hover { border-color: ${colors.brand}; }
`

const chipActive = css`
  ${chip};
  background: ${colors.brand};
  border-color: ${colors.brand};
  color: white;
`

const chipSmall = css`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  background: ${colors.bgSidebar};
  border: 1px solid ${colors.border};
`

const promptExcerpt = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: ${space.xs};
  white-space: pre-wrap;
`

const presetActions = css`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`

const smallBtn = css`
  ${btnSecondary};
  font-size: ${font.size.xs};
  padding: ${space.xs} ${space.sm};
`

const formRow = css`
  margin-bottom: ${space.md};
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

const formCard = css`
  ${card};
  margin-bottom: ${space.sm};
`

const addBtnStyle = css`
  ${btnSecondary};
  font-size: ${font.size.sm};
  margin-top: ${space.sm};
`

const selectInput = css`
  ${input};
  appearance: auto;
`

const addCustomRow = css`
  display: flex;
  gap: ${space.xs};
  margin-top: ${space.xs};
`

const smallInput = css`
  ${input};
  flex: 1;
  font-size: ${font.size.sm};
  padding: ${space.xs} ${space.sm};
`

const emptyText = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  padding: ${space.md};
  text-align: center;
`

const extractBtn = css`
  ${btnSecondary};
  font-size: ${font.size.sm};
  margin-top: ${space.sm};
  margin-left: ${space.sm};
`

const placeholderMsg = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  padding: ${space.sm} ${space.md};
  background: ${colors.bgSidebar};
  border-radius: 6px;
  margin-top: ${space.sm};
`

/**
 * StyleEditor component
 *
 * attrs:
 *   stylePresets: [{ id, label, form, tone, custom_prompt }]
 *   onCreatePreset: (data) => Promise
 *   onUpdatePreset: (presetId, data) => Promise
 *   onDeletePreset: (presetId) => Promise
 *   onExtractStyles: () => Promise
 *   saving: boolean
 *   extracting: boolean
 */
export function StyleEditor() {
  let showCreate = false
  let editingId = null

  // Form state
  let form = { label: '', form: '', customForm: '', tones: [], customTone: '', customPrompt: '', examples: [], newExample: '' }

  function resetForm() {
    form = { label: '', form: '', customForm: '', tones: [], customTone: '', customPrompt: '', examples: [], newExample: '' }
  }

  function initFormFromPreset(preset) {
    const toneList = preset.tone ? preset.tone.split(',').map(t => t.trim()).filter(Boolean) : []
    const formValue = FORM_OPTIONS.includes(preset.form) ? preset.form : (preset.form ? 'other' : '')
    let parsedExamples = []
    if (preset.examples) {
      try { parsedExamples = JSON.parse(preset.examples) } catch { /* ignore */ }
    }
    form = {
      label: preset.label || '',
      form: formValue,
      customForm: formValue === 'other' ? preset.form : '',
      tones: toneList,
      customTone: '',
      customPrompt: preset.custom_prompt || '',
      examples: parsedExamples,
      newExample: '',
    }
  }

  function toggleTone(tone) {
    const idx = form.tones.indexOf(tone)
    if (idx >= 0) {
      form.tones.splice(idx, 1)
    } else {
      form.tones.push(tone)
    }
  }

  function addCustomTone() {
    const t = form.customTone.trim()
    if (t && !form.tones.includes(t)) {
      form.tones.push(t)
      form.customTone = ''
    }
  }

  function getFormData() {
    const resolvedForm = form.form === 'other' ? form.customForm.trim() : form.form
    return {
      label: form.label.trim(),
      form: resolvedForm || null,
      tone: form.tones.length > 0 ? form.tones.join(', ') : null,
      customPrompt: form.customPrompt.trim() || null,
      examples: form.examples.length > 0 ? form.examples : null,
    }
  }

  function addExample() {
    const text = form.newExample.trim()
    if (text && form.examples.length < 5) {
      form.examples.push(text)
      form.newExample = ''
    }
  }

  function removeExample(index) {
    form.examples.splice(index, 1)
  }

  return {
    view(vnode) {
      const {
        stylePresets = [],
        onCreatePreset,
        onUpdatePreset,
        onDeletePreset,
        onExtractStyles,
        saving,
        extracting,
        readOnly,
      } = vnode.attrs

      const children = []

      // --- Existing presets ---
      if (stylePresets.length > 0) {
        for (const preset of stylePresets) {
          const isEditing = editingId === preset.id

          if (isEditing) {
            // Inline edit form
            children.push(renderForm({
              key: `edit-${preset.id}`,
              saving,
              onSave: async () => {
                await onUpdatePreset(preset.id, getFormData())
                editingId = null
                resetForm()
              },
              onCancel: () => { editingId = null; resetForm() },
              submitLabel: 'Update',
            }))
          } else {
            // Preset card
            const toneList = preset.tone ? preset.tone.split(',').map(t => t.trim()).filter(Boolean) : []
            children.push(m('div', { key: preset.id, class: presetCard }, [
              m('div', { class: presetInfo }, [
                m('div', { class: presetLabel }, preset.label),
                m('div', (() => {
                  const badges = []
                  if (preset.form) badges.push(m('span', { key: '__form', class: badgeStyle }, preset.form))
                  for (const t of toneList) badges.push(m('span', { key: t, class: chipSmall }, t))
                  return badges
                })()),
                preset.custom_prompt
                  ? m('div', { class: promptExcerpt }, preset.custom_prompt)
                  : null,
                (() => {
                  if (!preset.examples) return null
                  let count = 0
                  try { count = JSON.parse(preset.examples).length } catch { return null }
                  if (count === 0) return null
                  return m('div', { style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; margin-top: ${space.xs};` }, `${count} example${count !== 1 ? 's' : ''}`)
                })(),
              ]),
              !readOnly ? m('div', { class: presetActions }, [
                m('button', {
                  class: smallBtn,
                  onclick: () => {
                    editingId = preset.id
                    showCreate = false
                    initFormFromPreset(preset)
                  },
                }, 'Edit'),
                m('button', {
                  class: smallBtn,
                  disabled: saving,
                  onclick: () => onDeletePreset(preset.id),
                }, 'Delete'),
              ]) : null,
            ]))
          }
        }
      } else {
        children.push(m('div', { key: 'empty', class: emptyText }, readOnly ? 'No style presets configured for this project.' : 'No style presets yet. Create one below.'))
      }

      // --- Create button / form ---
      if (!readOnly && !showCreate && !editingId) {
        const actionRow = []
        actionRow.push(m('button', {
          key: 'create-btn',
          class: addBtnStyle,
          onclick: () => { showCreate = true; resetForm() },
        }, '+ Add Style Preset'))

        children.push(m('div', { key: 'action-row', style: 'display: flex; align-items: center;' }, actionRow))
      }

      if (!readOnly && showCreate) {
        children.push(renderForm({
          key: 'create-form',
          saving,
          onSave: async () => {
            await onCreatePreset(getFormData())
            showCreate = false
            resetForm()
          },
          onCancel: () => { showCreate = false; resetForm() },
          submitLabel: 'Create',
        }))
      }

      return m('div', children)
    },
  }

  function renderForm({ key, saving, onSave, onCancel, submitLabel }) {
    return m('div', { key, class: formCard }, [
      m('div', { class: formRow }, [
        m('label', { class: label }, 'Label'),
        m('input', {
          class: input,
          value: form.label,
          oninput: (e) => { form.label = e.target.value },
          placeholder: 'e.g., Formal email',
        }),
      ]),

      m('div', { class: formRow }, [
        m('label', { class: label }, 'Form'),
        m('select', {
          class: selectInput,
          value: form.form,
          onchange: (e) => { form.form = e.target.value },
        }, [
          m('option', { key: '__placeholder', value: '' }, '-- Select form --'),
          ...FORM_OPTIONS.map(f => m('option', { key: f, value: f }, f.replace(/_/g, ' '))),
          m('option', { key: '__other', value: 'other' }, 'Other'),
        ]),
        form.form === 'other'
          ? m('input', {
              class: css`${input}; margin-top: ${space.xs};`,
              value: form.customForm,
              oninput: (e) => { form.customForm = e.target.value },
              placeholder: 'Custom form...',
            })
          : null,
      ]),

      m('div', { class: formRow }, [
        m('label', { class: label }, 'Tone'),
        m('div', { class: chipWrap },
          TONE_OPTIONS.map(t =>
            m('span', {
              key: t,
              class: form.tones.includes(t) ? chipActive : chip,
              onclick: () => toggleTone(t),
            }, t.replace(/_/g, ' '))
          )
        ),
        // Show custom tones that are not in TONE_OPTIONS
        (() => {
          const customTones = form.tones.filter(t => !TONE_OPTIONS.includes(t))
          if (customTones.length === 0) return null
          return m('div', { class: chipWrap, style: `margin-top: 4px;` },
            customTones.map(t =>
              m('span', {
                key: `custom-${t}`,
                class: chipActive,
                onclick: () => toggleTone(t),
              }, [t, ' \u00d7'])
            )
          )
        })(),
        m('div', { class: addCustomRow }, [
          m('input', {
            class: smallInput,
            value: form.customTone,
            oninput: (e) => { form.customTone = e.target.value },
            placeholder: 'Add custom tone...',
            onkeydown: (e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addCustomTone()
              }
            },
          }),
          m('button', {
            class: smallBtn,
            type: 'button',
            onclick: addCustomTone,
          }, 'Add'),
        ]),
      ]),

      m('div', { class: formRow }, [
        m('label', { class: label }, 'Custom Prompt (optional)'),
        m('textarea', {
          class: input,
          rows: 3,
          value: form.customPrompt,
          oninput: (e) => { form.customPrompt = e.target.value },
          placeholder: 'Additional generation instructions...',
        }),
      ]),

      m('div', { class: formRow }, (() => {
        const exChildren = []
        exChildren.push(m('label', { key: 'ex-label', class: label }, `Example Messages (${form.examples.length}/5)`))
        for (let i = 0; i < form.examples.length; i++) {
          exChildren.push(m('div', { key: `ex-${i}`, style: `display: flex; gap: ${space.xs}; margin-bottom: ${space.xs};` }, [
            m('div', {
              style: `flex: 1; font-size: ${font.size.sm}; padding: ${space.sm}; background: ${colors.bgSidebar}; border: 1px solid ${colors.border}; border-radius: 4px; white-space: pre-wrap;`,
            }, form.examples[i]),
            m('button', {
              class: smallBtn,
              type: 'button',
              onclick: () => removeExample(i),
            }, '\u00d7'),
          ]))
        }
        if (form.examples.length < 5) {
          exChildren.push(m('div', { key: 'ex-add', style: `display: flex; gap: ${space.xs}; margin-top: ${space.xs};` }, [
            m('textarea', {
              class: smallInput,
              rows: 2,
              value: form.newExample,
              oninput: (e) => { form.newExample = e.target.value },
              placeholder: 'Paste or type an example message...',
            }),
            m('button', {
              class: smallBtn,
              type: 'button',
              disabled: !form.newExample.trim(),
              onclick: addExample,
            }, 'Add'),
          ]))
        }
        return exChildren
      })()),

      m('div', { class: formActions }, [
        m('button', {
          class: btnSecondary,
          type: 'button',
          onclick: onCancel,
        }, 'Cancel'),
        m('button', {
          class: btnPrimary,
          disabled: saving || !form.label.trim(),
          onclick: onSave,
        }, saving ? 'Saving...' : submitLabel),
      ]),
    ])
  }
}
